/**
 * Footer (Solid.js) – Fixe Leiste unten mit Uhr und Version
 * Version kommt aus der Admin-Einstellung (GET /api/app-version).
 * Styles in global.css: .app-footer-*
 */
import { createSignal, onMount } from 'solid-js';
import { A } from '@solidjs/router';
import axios from '../config/axios';
import { useTranslation } from '../context/LanguageContext';

const FALLBACK_VERSION = 'v0.7.0 alpha';

function formatTime(date: Date): string {
  const h = date.getHours().toString().padStart(2, '0');
  const m = date.getMinutes().toString().padStart(2, '0');
  const s = date.getSeconds().toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function formatDate(date: Date): string {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

interface FooterProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

export default function Footer(props: FooterProps) {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = createSignal(new Date());
  const [appVersion, setAppVersion] = createSignal(FALLBACK_VERSION);

  onMount(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    axios.get<{ success: boolean; data?: { version: string } }>('/api/app-version')
      .then((res) => {
        if (res.data?.success && res.data.data?.version) {
          setAppVersion(res.data.data.version);
        }
      })
      .catch(() => {});
    return () => clearInterval(timer);
  });

  return (
    <footer class="app-footer" role="contentinfo">
      <div class="app-footer-spacer" aria-hidden>
        {props.onToggle && (
          <button
            type="button"
            class="app-footer-toggle-btn"
            onClick={props.onToggle}
            aria-label={props.isOpen ? t('footer.closeFooterLinks') : t('footer.openFooterLinks')}
            title={props.isOpen ? t('footer.closeFooter') : t('footer.openFooter')}
          >
            <span class={`app-footer-toggle-chevron${props.isOpen ? ' app-footer-toggle-chevron--open' : ''}`}>
              ▲
            </span>
            <span class="app-footer-toggle-label">{props.isOpen ? t('footer.lessLinks') : t('footer.moreLinks')}</span>
          </button>
        )}
      </div>
      <div class="app-footer-time-section">
        <span class="app-footer-clock-icon" aria-hidden>🕐</span>
        <div class="app-footer-time-text">
          <span class="app-footer-time">{formatTime(currentTime())}</span>
          <span class="app-footer-date">{formatDate(currentTime())}</span>
        </div>
      </div>
      <div class="app-footer-version-wrap">
        <A href="/bug-report" class="app-footer-bug-btn" title={t('footer.reportBug')}>
          <i class="fas fa-bug" /> <span class="app-footer-bug-text">{t('footer.reportBug')}</span>
        </A>
        <span class="app-footer-version" title="App-Version">{appVersion()}</span>
      </div>
    </footer>
  );
}
